import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import { useGSAP } from '@gsap/react';
import ImageCard from './ImageCard';
import { useTheme } from '../hooks/useTheme';

gsap.registerPlugin(Observer);

const ArrowIcon = ({ className = '' }) => (
  <svg
    className={className}
    width='8'
    height='14'
    viewBox='0 0 8 14'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1 1L7 7L1 13'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const CardsCarousel = ({ cards = [], className = '' }) => {
  const { isDarkMode } = useTheme();
  const carouselRef = useRef(null);
  const wrapperRef = useRef(null);
  const position = useRef({ x: 0 });
  const itemWidthWithGap = useRef(0);
  const apiRef = useRef({ next: () => {}, prev: () => {} });

  useGSAP(
    () => {
      const carousel = carouselRef.current;
      const wrapper = wrapperRef.current;

      if (!carousel || !wrapper || cards.length === 0) return;

      const allCards = gsap.utils.toArray('.cards-carousel-item', wrapper);
      if (allCards.length === 0) return;

      let activeAnimation;
      let currentIndex = 0;
      let lastActiveIndex = -1;
      let pressProxy = 0;
      let wheelTimeout;

      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: `(min-width: 769px)`,
          isMobile: `(max-width: 768px)`,
        },
        (context) => {
          const { isDesktop } = context.conditions;

          const baseWidth = isDesktop ? '29.80dvw' : '82dvw';
          const activeWidth = isDesktop ? baseWidth : '82.78dvw';
          const gap = isDesktop ? 20 : 10;
          const PUSH_RANGE = 8;

          const getDvwInPx = (dvwString) => {
            const value = parseFloat(dvwString);
            return window.innerWidth * (value / 100);
          };

          const ACTIVE_SWITCH_THRESHOLD = 0.2;

          const getActiveFlatIndex = (x) => {
            const rawIndex = x / itemWidthWithGap.current;
            const flooredIndex = Math.floor(rawIndex);
            const fraction = rawIndex - flooredIndex;
            return fraction >= ACTIVE_SWITCH_THRESHOLD
              ? flooredIndex + 1
              : flooredIndex;
          };

          let maxX = 0;
          const clampX = (x) => gsap.utils.clamp(0, maxX, x);

          const applyCardStates = (activeFlatIndex, animate) => {
            const baseWidthInPx = getDvwInPx(baseWidth);
            const activeWidthInPx = getDvwInPx(activeWidth);
            const pushOffset = activeWidthInPx - baseWidthInPx;

            allCards.forEach((item, domIndex) => {
              const delta = domIndex - activeFlatIndex;
              const isActive = delta === 0;
              const isPushed = delta > 0 && delta <= PUSH_RANGE;

              const vars = {
                width: isActive ? activeWidthInPx : baseWidthInPx,
                x: isPushed ? pushOffset : 0,
              };

              if (animate) {
                gsap.to(item.querySelector('.card-content'), {
                  ...vars,
                  duration: 0.4,
                  ease: 'power2.out',
                  overwrite: 'auto',
                });
              } else {
                gsap.set(item.querySelector('.card-content'), vars);
              }
            });
          };

          const calculateSizes = () => {
            const baseWidthInPx = getDvwInPx(baseWidth);
            itemWidthWithGap.current = baseWidthInPx + gap;

            gsap.set(wrapper, { gap: `${gap}px` });
            gsap.set(allCards, { width: baseWidthInPx });

            const carouselStyles = getComputedStyle(carousel);
            const visibleWidth =
              carousel.clientWidth -
              parseFloat(carouselStyles.paddingLeft) -
              parseFloat(carouselStyles.paddingRight);

            const totalContentWidth =
              cards.length * baseWidthInPx + (cards.length - 1) * gap;
            maxX = Math.max(0, totalContentWidth - visibleWidth);

            applyCardStates(currentIndex, false);
            lastActiveIndex = currentIndex;
          };

          const updatePosition = () => {
            gsap.set(wrapper, { x: -position.current.x });

            const activeFlatIndex = getActiveFlatIndex(position.current.x);

            if (activeFlatIndex === lastActiveIndex) return;

            lastActiveIndex = activeFlatIndex;
            applyCardStates(activeFlatIndex, true);
          };

          const animateToX = (rawX) => {
            activeAnimation?.kill();

            const targetX = clampX(rawX);
            currentIndex = gsap.utils.clamp(
              0,
              cards.length - 1,
              Math.round(targetX / itemWidthWithGap.current),
            );

            const distance = Math.abs(position.current.x - targetX);
            const dynamicDuration = gsap.utils.clamp(
              0.45,
              0.9,
              distance / 1400,
            );

            activeAnimation = gsap.to(position.current, {
              x: targetX,
              duration: dynamicDuration,
              ease: 'power2.out',
              onUpdate: updatePosition,
            });
          };

          const animateToCard = (index) => {
            const clampedIndex = gsap.utils.clamp(0, cards.length - 1, index);
            animateToX(clampedIndex * itemWidthWithGap.current);
          };

          apiRef.current = {
            next: () =>
              animateToX(position.current.x + itemWidthWithGap.current),
            prev: () =>
              animateToX(position.current.x - itemWidthWithGap.current),
          };

          calculateSizes();
          position.current.x = currentIndex * itemWidthWithGap.current;
          updatePosition();

          const observer = Observer.create({
            target: carousel,
            type: 'wheel,pointer,touch',
            dragMinimum: 5,
            onPress: (self) => {
              if (self.type === 'pointer' || self.type === 'touch') {
                self.event.preventDefault();
              }
              activeAnimation?.kill();
              pressProxy = position.current.x;
              carousel.style.cursor = 'grabbing';
            },
            onDrag: (self) => {
              if (Math.abs(self.deltaX) > Math.abs(self.deltaY)) {
                self.event.preventDefault();
                position.current.x = clampX(
                  pressProxy - (self.x - self.startX),
                );
                updatePosition();
              }
            },
            onDragEnd: (self) => {
              carousel.style.cursor = 'grab';
              let closestIndex = getActiveFlatIndex(position.current.x);

              const flickVelocity = 500;
              if (Math.abs(self.velocityX) > flickVelocity) {
                closestIndex += self.velocityX < 0 ? 1 : -1;
              }
              animateToCard(closestIndex);
            },
            onWheel: (self) => {
              if (isDesktop) {
                if (Math.abs(self.deltaX) > Math.abs(self.deltaY)) {
                  self.event.preventDefault();
                  activeAnimation?.kill();

                  position.current.x = clampX(position.current.x + self.deltaX);
                  updatePosition();

                  clearTimeout(wheelTimeout);
                  wheelTimeout = setTimeout(() => {
                    animateToCard(getActiveFlatIndex(position.current.x));
                  }, 800);
                }
              }
            },
          });

          const resizeHandler = () => {
            activeAnimation?.kill();
            calculateSizes();
            animateToCard(currentIndex);
          };
          window.addEventListener('resize', resizeHandler);

          return () => {
            observer.kill();
            activeAnimation?.kill();
            clearTimeout(wheelTimeout);
            window.removeEventListener('resize', resizeHandler);
          };
        },
      );
    },
    {
      scope: carouselRef,
      dependencies: [cards.length],
    },
  );

  return (
    <div className='cards-carousel-wrapper'>
      <div
        ref={carouselRef}
        className={`cards-carousel overflow-hidden relative page-wrapper-x ${className}`}
        style={{ cursor: 'grab', touchAction: 'pan-y' }}
      >
        <div ref={wrapperRef} className='flex cards-wrapper full-height'>
          {cards.map((card, index) => (
            <div
              key={card._key ?? index}
              className='cards-carousel-item'
              style={{ flexShrink: 0 }}
            >
              <div className='card-content'>
                <ImageCard
                  {...card}
                  imageUrl={
                    isDarkMode
                      ? card.darkModeImageUrl || card.lightModeImageUrl
                      : card.lightModeImageUrl
                  }
                  buttonVariant={isDarkMode ? '' : 'dark'}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className='cards-carousel-nav page-wrapper-x'>
        <button
          type='button'
          className='cards-carousel-arrow cards-carousel-arrow-prev'
          aria-label='Previous slide'
          onClick={() => apiRef.current.prev()}
        >
          <ArrowIcon />
        </button>
        <button
          type='button'
          className='cards-carousel-arrow cards-carousel-arrow-next'
          aria-label='Next slide'
          onClick={() => apiRef.current.next()}
        >
          <ArrowIcon />
        </button>
      </div>
    </div>
  );
};

export default CardsCarousel;
